<template>
  <v-card flat class="text-left" style="background: transparent; max-width: 100%;">
    <div class="row mb-9">
      <div class="col-lg-6 offset-lg-3">
        <h1 class="text-left fw-normal mb-4 main-heading text-center">
          Affordable options that beat any driving school in Ontario — with Klarna payment plans available.
        </h1>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="text-left py-5">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-3">Loading packages...</p>
    </div>

           <!-- Packages List -->
      <div v-else-if="packages.length > 0" class="package-grid">
        <!-- Top row with 2 packages -->
        <div class="top-row">
          <div v-for="pkg in packages.slice(0, 2)" :key="pkg.objectId" class="package-card">
            <div class="package-container" :class="{
              'premium': pkg.isPremium,
              'popular': pkg.isPopular,
              'best-value': pkg.isBestValue,
              'popular-highlight': pkg.isPopular
            }">
              <!-- Badge + Offer Timer -->
              <div class="badge-row">
                <span v-if="pkg.isPopular" class="package-badge popular-badge">MOST POPULAR</span>
                <span v-else-if="pkg.isPremium" class="package-badge premium-badge">PREMIUM</span>
                <span v-else-if="pkg.isBestValue" class="package-badge best-value-badge">BEST VALUE</span>
                <span v-else-if="pkg.isNew" class="package-badge best-value-badge">NEW</span>
                <div v-if="isDiscountActive(pkg)" class="offer-timer">
                  <span class="offer-dot"></span>
                  <span class="offer-label">OFFER ENDS IN</span>
                  <template v-if="countdown(pkg).showDays">
                    <span class="time-chip">{{ countdown(pkg).d }}d</span>
                    <span class="time-sep">:</span>
                  </template>
                  <span class="time-chip">{{ countdown(pkg).h }}</span>
                  <span class="time-sep">:</span>
                  <span class="time-chip">{{ countdown(pkg).m }}</span>
                  <span class="time-sep">:</span>
                  <span class="time-chip">{{ countdown(pkg).s }}</span>
                </div>
              </div>

              <!-- Header with Title and Icon -->
              <div class="package-header">
                <h3 class="package-title">{{ pkg.title }}</h3>
                <img :src="getPackageIcon(pkg.title)" :alt="getPackageIconAlt(pkg.title)" class="package-icon" />
              </div>

              <!-- Price Section -->
              <div class="price-section">
                <div class="price-row">
                  <span class="current-price">${{ getCurrentPrice(pkg) }}</span>
                  <span v-if="isDiscountActive(pkg)" class="original-price">${{ pkg.originalPrice }}</span>
                  <span v-if="isDiscountActive(pkg)" class="save-badge">SAVE {{ getSavePercent(pkg) }}%</span>
                  <span class="tax-badge" :class="{ 'tax-applicable': pkg.taxApplicable, 'tax-free': !pkg.taxApplicable }">
                    {{ pkg.taxApplicable ? 'TAX APPLICABLE' : 'TAX FREE' }}
                  </span>
                </div>
                <div v-if="isDiscountActive(pkg)" class="offer-ends-banner">
                  <span class="offer-bolt">⚡</span>
                  <span class="offer-text">This offer ends in</span>
                  <template v-if="countdown(pkg).showDays">
                    <span class="time-chip dark">{{ countdown(pkg).d }}<sub>D</sub></span>
                    <span class="time-sep">:</span>
                  </template>
                  <span class="time-chip dark">{{ countdown(pkg).h }}<sub>H</sub></span>
                  <span class="time-sep">:</span>
                  <span class="time-chip dark">{{ countdown(pkg).m }}<sub>M</sub></span>
                  <span class="time-sep">:</span>
                  <span class="time-chip dark">{{ countdown(pkg).s }}<sub>S</sub></span>
                </div>
                <p v-if="getPaymentInfo(pkg)" class="payment-info" v-html="getPaymentInfo(pkg)"></p>
              </div>

              <!-- Features List -->
              <ul class="features-list">
                <li v-for="(feature, index) in getPackageFeatures(pkg)" :key="index" class="feature-item">
                  <span class="feature-icon">{{ getFeatureIcon(feature) }}</span>
                  <span v-html="feature"></span>
                </li>
              </ul>

              <!-- Action Button -->
              <button type="button" @click="selectPackageOption(pkg.objectId)" class="package-button" :class="getButtonClass(pkg)">
                {{ pkg.buttonText || getButtonText(pkg) }}
              </button>
            </div>
          </div>
        </div>

        <!-- Bottom row with 3 packages -->
        <div class="bottom-row">
          <div v-for="pkg in packages.slice(2)" :key="pkg.objectId" class="package-card">
            <div class="package-container" :class="{
              'premium': pkg.isPremium,
              'popular': pkg.isPopular,
              'best-value': pkg.isBestValue,
              'popular-highlight': pkg.isPopular
            }">
              <!-- Badge + Offer Timer -->
              <div class="badge-row">
                <span v-if="pkg.isPopular" class="package-badge popular-badge">MOST POPULAR</span>
                <span v-else-if="pkg.isPremium" class="package-badge premium-badge">PREMIUM</span>
                <span v-else-if="pkg.isBestValue" class="package-badge best-value-badge">BEST VALUE</span>
                <span v-else-if="pkg.isNew" class="package-badge best-value-badge">NEW</span>
                <div v-if="isDiscountActive(pkg)" class="offer-timer">
                  <span class="offer-dot"></span>
                  <span class="offer-label">OFFER ENDS IN</span>
                  <template v-if="countdown(pkg).showDays">
                    <span class="time-chip">{{ countdown(pkg).d }}d</span>
                    <span class="time-sep">:</span>
                  </template>
                  <span class="time-chip">{{ countdown(pkg).h }}</span>
                  <span class="time-sep">:</span>
                  <span class="time-chip">{{ countdown(pkg).m }}</span>
                  <span class="time-sep">:</span>
                  <span class="time-chip">{{ countdown(pkg).s }}</span>
                </div>
              </div>

              <!-- Header with Title and Icon -->
              <div class="package-header">
                <h3 class="package-title">{{ pkg.title }}</h3>
                <img :src="getPackageIcon(pkg.title)" :alt="getPackageIconAlt(pkg.title)" class="package-icon" />
              </div>

              <!-- Price Section -->
              <div class="price-section">
                <div class="price-row">
                  <span class="current-price">${{ getCurrentPrice(pkg) }}</span>
                  <span v-if="isDiscountActive(pkg)" class="original-price">${{ pkg.originalPrice }}</span>
                  <span v-if="isDiscountActive(pkg)" class="save-badge">SAVE {{ getSavePercent(pkg) }}%</span>
                  <span class="tax-badge" :class="{ 'tax-applicable': pkg.taxApplicable, 'tax-free': !pkg.taxApplicable }">
                    {{ pkg.taxApplicable ? 'TAX APPLICABLE' : 'TAX FREE' }}
                  </span>
                </div>
                <div v-if="isDiscountActive(pkg)" class="offer-ends-banner">
                  <span class="offer-bolt">⚡</span>
                  <span class="offer-text">This offer ends in</span>
                  <template v-if="countdown(pkg).showDays">
                    <span class="time-chip dark">{{ countdown(pkg).d }}<sub>D</sub></span>
                    <span class="time-sep">:</span>
                  </template>
                  <span class="time-chip dark">{{ countdown(pkg).h }}<sub>H</sub></span>
                  <span class="time-sep">:</span>
                  <span class="time-chip dark">{{ countdown(pkg).m }}<sub>M</sub></span>
                  <span class="time-sep">:</span>
                  <span class="time-chip dark">{{ countdown(pkg).s }}<sub>S</sub></span>
                </div>
                <p v-if="getPaymentInfo(pkg)" class="payment-info" v-html="getPaymentInfo(pkg)"></p>
              </div>

              <!-- Features List -->
              <ul class="features-list">
                <li v-for="(feature, index) in getPackageFeatures(pkg)" :key="index" class="feature-item">
                  <span class="feature-icon">{{ getFeatureIcon(feature) }}</span>
                  <span v-html="feature"></span>
                </li>
              </ul>

              <!-- Action Button -->
              <button type="button" @click="selectPackageOption(pkg.objectId)" class="package-button" :class="getButtonClass(pkg)">
                {{ pkg.buttonText || getButtonText(pkg) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- No Packages Message -->
      <div v-else-if="packages.length === 0" class="text-left py-5">
        <p>No packages available at the moment.</p>
      </div>

         <!-- Payment Modal - Commented out for now -->
     <!-- <div v-if="showModal" id="customModal" class="modal" @click.self="closeModal">
       <div class="modal-content">
         <span class="close-btn" @click="closeModal">&times;</span>
         <h1>Payment Plan</h1>
         <h4>Buy Now, Pay with driverX at your own pace.</h4>
         <p>Get what you love, choose how you pay.</p>

         <ul class="paymentPlans">
           <li>
             <label class="box" :class="{ selected: selectedOption === 'payInFull' }" for="fullPayment">
               <input type="radio" id="fullPayment" value="payInFull" v-model="selectedOption" name="paymentPlan" />
               <div class="flewRow">
                 <h4>${{ selectedPackageFinalPrice }}.00</h4>
                 <div class="tag">Pay in full</div>
               </div>
               <p>Pay Now</p>
             </label>
           </li>
           <li>
             <label class="box" :class="{ selected: selectedOption === 'payInFive' }" for="installMent">
               <input type="radio" id="installMent" value="payInFive" v-model="selectedOption" name="paymentPlan" />
               <div class="flewRow">
                 <h4>${{ Math.round(selectedPackageFinalPrice / 5) }}</h4>
               </div>
               <p>Pay in 5</p>
               <div class="imgBox">
                 <div class="installment-graph">
                   <div class="installment-item Today">
                     <div class="progress-circle"
                       :style="{ background: 'conic-gradient(#2D9A47 0deg 72deg, #F7F8FA 72deg 360deg)' }"></div>
                     <div class="installment-text">
                       <div class="amount">${{ Math.round(selectedPackageFinalPrice / 5) }}</div>
                       <div class="period">Today</div>
                     </div>
                   </div>
                   <div class="installment-item">
                     <div class="progress-circle"
                       :style="{ background: 'conic-gradient(#2D9A47 0deg 144deg, #F7F8FA 144deg 360deg)' }"></div>
                     <div class="installment-text">
                       <div class="amount">${{ Math.round(selectedPackageFinalPrice / 5) }}</div>
                       <div class="period">In 2 weeks</div>
                     </div>
                   </div>
                   <div class="installment-item">
                     <div class="progress-circle"
                       :style="{ background: 'conic-gradient(#2D9A47 0deg 216deg, #F7F8FA 216deg 360deg)' }"></div>
                     <div class="installment-text">
                       <div class="amount">${{ Math.round(selectedPackageFinalPrice / 5) }}</div>
                       <div class="period">In 4 weeks</div>
                     </div>
                   </div>
                   <div class="installment-item">
                     <div class="progress-circle"
                       :style="{ background: 'conic-gradient(#2D9A47 0deg 288deg, #F7F8FA 288deg 360deg)' }"></div>
                     <div class="installment-text">
                       <div class="amount">${{ Math.round(selectedPackageFinalPrice / 5) }}</div>
                       <div class="period">In 6 weeks</div>
                     </div>
                   </div>
                   <div class="installment-item">
                     <div class="progress-circle"
                       :style="{ background: 'conic-gradient(#2D9A47 0deg 360deg, #F7F8FA 360deg 360deg)' }"></div>
                     <div class="installment-text">
                       <div class="amount">${{ Math.round(selectedPackageFinalPrice / 5) }}</div>
                       <div class="period">In 8 weeks</div>
                     </div>
                   </div>
                 </div>
               </div>
               <div class="flewRow">
                 <div>
                   <small>Apr</small>
                   <h5 class="text-green">0%</h5>
                 </div>
                 <div class="text-center">
                   <small>Interest</small>
                   <h5 class="text-green">Free</h5>
                 </div>
                 <div class="text-right">
                   <small>Total</small>
                   <h5>${{ selectedPackageFinalPrice }}.00</h5>
                 </div>
               </div>
             </label>
           </li>
         </ul>

         <div class="btnRow">
           <button type="button" @click="handlePaymentSelection" class="btn btn-green" id="checkoutBtn">Checkout</button>
           <button type="button" @click="closeModal" class="btn close-modal-btn btn-green" id="closeModal">Close</button>
         </div>
       </div>
     </div> -->
  </v-card>
</template>

<script>
import Parse from 'parse'

export default {
  name: 'PackagePlanBoxes',
  data () {
    return {
      loading: true,
      packages: [],
      showModal: false,
      selectedOption: 'payInFull',
      selectedPremiumPackage: null,
      now: Date.now(),
      _tickInterval: null
    }
  },
  beforeDestroy () {
    if (this._tickInterval) clearInterval(this._tickInterval)
  },
  computed: {
    selectedPackageFinalPrice () {
      if (!this.selectedPremiumPackage) return ''
      if (this.selectedPremiumPackage.discountPercentage) {
        const discountedPrice = Math.round(this.selectedPremiumPackage.price * (1 - this.selectedPremiumPackage.discountPercentage / 100))
        console.log('Original Price:', this.selectedPremiumPackage.price)
        console.log('Discount %:', this.selectedPremiumPackage.discountPercentage)
        console.log('Discounted Price:', discountedPrice)
        return discountedPrice
      }
      return this.selectedPremiumPackage.price || ''
    },
    selectedPackageSavings () {
      if (!this.selectedPremiumPackage || !this.selectedPremiumPackage.discountPercentage) return 0
      const savings = Math.round(this.selectedPremiumPackage.price * (this.selectedPremiumPackage.discountPercentage / 100))
      console.log('Savings calculation:', {
        price: this.selectedPremiumPackage.price,
        discountPercentage: this.selectedPremiumPackage.discountPercentage,
        calculatedSavings: savings
      })
      return savings
    },
    checkoutUrl () {
      return this.selectedOption === 'payInFull' ? this.fullPaymentURL : this.installmentURL
    }
  },
  async mounted () {
    await this.fetchPackages()
    this._tickInterval = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  methods: {
    async fetchPackages () {
      try {
        this.loading = true

        // Use Parse Cloud Function
        const result = await Parse.Cloud.run('getAllSchoolPackages')

        if (result && result.packages) {
          this.packages = result.packages
        } else {
          console.error('No packages found in response:', result)
          this.packages = []
        }
      } catch (error) {
        console.error('Error fetching packages:', error)
        this.packages = []
      } finally {
        this.loading = false
      }
    },
    closeModal () {
      this.showModal = false
      this.selectedPremiumPackage = null
    },
    openPaymentModal (pkg) {
      if (!pkg) {
        console.error('No package provided to openPaymentModal')
        // return;
      }
      this.selectedPremiumPackage = pkg
      this.showModal = true
    },
    selectPackageOption (packageId, priceId = null) {
      console.log('selectPackageOption called with:', { packageId, priceId })
      const selectedPackage = this.packages.find(pkg => pkg.objectId === packageId)
      console.log('Found package:', selectedPackage)

      // Commented out modal logic for now - all packages work like regular packages
      // if (selectedPackage && (selectedPackage.paymentPlan || selectedPackage.isPremium)) {
      //   console.log('Opening payment modal for premium/payment plan package');
      //   this.selectedPremiumPackage = selectedPackage;
      //   this.showModal = true;
      //   return;
      // }

      console.log('Emitting package selection for package')
      const discountActive = selectedPackage ? this.isDiscountActive(selectedPackage) : false
      const priceToUse = selectedPackage ? this.getCurrentPrice(selectedPackage) : 0
      const originalPrice = selectedPackage
        ? (selectedPackage.originalPrice != null ? selectedPackage.originalPrice : selectedPackage.price)
        : 0

      this.$emit('packageSelected', {
        objectId: packageId,
        priceId: priceId,
        title: selectedPackage ? selectedPackage.title : 'Package',
        price: priceToUse,
        originalPrice: originalPrice,
        effectivePrice: selectedPackage ? selectedPackage.effectivePrice : null,
        discounted: discountActive,
        discountEnd: selectedPackage ? selectedPackage.discountEnd : null,
        taxApplicable: selectedPackage ? selectedPackage.taxApplicable : false,
        freeBookings: selectedPackage ? selectedPackage.freeBookings : 0,
        packageDetails: selectedPackage ? selectedPackage.packageDetails : '',
        prePaid: selectedPackage ? selectedPackage.prePaid : false,
        schoolId: selectedPackage ? selectedPackage.schoolId : null
      })
    },
    handlePaymentSelection () {
      if (!this.selectedPremiumPackage) {
        console.error('No premium package selected')
        // return;
      }

      try {
        const packageId = this.selectedPremiumPackage.objectId
        const priceId = this.selectedOption === 'payInFive' ? 'price_1QrJzIL9XL9PNUhenO7FyxsF' : null

        this.closeModal()

        // Emit the selected package data with payment plan information
        this.$emit('packageSelected', {
          objectId: packageId,
          priceId: priceId
        })
      } catch (error) {
        console.error('Error in handlePaymentSelection:', error)
        this.closeModal()
      }
    },
    // New methods for the updated design
    getDiscountEndMs (pkg) {
      const end = pkg.discountEnd
      if (!end) return 0
      let t
      if (end instanceof Date) {
        t = end.getTime()
      } else if (typeof end === 'number') {
        t = end
      } else if (typeof end === 'string') {
        t = new Date(end).getTime()
      } else if (end.iso) {
        t = new Date(end.iso).getTime()
      } else {
        t = NaN
      }
      return isNaN(t) ? 0 : t
    },
    isDiscountActive (pkg) {
      if (pkg.discounted !== true) return false
      const endMs = this.getDiscountEndMs(pkg)
      return endMs > this.now
    },
    getCurrentPrice (pkg) {
      if (this.isDiscountActive(pkg)) {
        return pkg.effectivePrice != null ? pkg.effectivePrice : pkg.price
      }
      return pkg.originalPrice != null ? pkg.originalPrice : (pkg.price || '-')
    },
    getSavings (pkg) {
      if (!this.isDiscountActive(pkg)) return 0
      const orig = pkg.originalPrice != null ? pkg.originalPrice : pkg.price
      const eff = pkg.effectivePrice != null ? pkg.effectivePrice : pkg.price
      return Math.max(0, Math.round(orig - eff))
    },
    getSavePercent (pkg) {
      if (!this.isDiscountActive(pkg)) return 0
      const orig = pkg.originalPrice != null ? pkg.originalPrice : pkg.price
      if (!orig) return 0
      const eff = pkg.effectivePrice != null ? pkg.effectivePrice : pkg.price
      return Math.round(((orig - eff) / orig) * 100)
    },
    countdown (pkg) {
      const endMs = this.getDiscountEndMs(pkg)
      let diff = Math.max(0, Math.floor((endMs - this.now) / 1000))
      const totalHours = Math.floor(diff / 3600)
      let d = 0
      let h = totalHours
      if (totalHours >= 24) {
        d = Math.floor(totalHours / 24)
        h = totalHours - d * 24
      }
      diff -= totalHours * 3600
      const m = Math.floor(diff / 60)
      const s = diff - m * 60
      const pad = (n) => String(n).padStart(2, '0')
      return { d: pad(d), h: pad(h), m: pad(m), s: pad(s), showDays: d > 0 }
    },
    getPaymentInfo (pkg) {
      if (pkg.packageDetailsDescription && pkg.packageDetailsDescription.trim() !== '') {
        return pkg.packageDetailsDescription
      }
      return null // Don't show anything if no description
    },
    getPackageIcon (title) {
      const lowerTitle = title.toLowerCase()
      if (lowerTitle.includes('bde') || lowerTitle.includes('beginner')) {
        return 'https://cdn-icons-png.flaticon.com/512/2436/2436636.png' // Education icon
      } else if (lowerTitle.includes('ultimate')) {
        return 'https://cdn-icons-png.flaticon.com/512/1828/1828640.png' // Star icon
      } else if (lowerTitle.includes('lesson')) {
        return 'https://cdn-icons-png.flaticon.com/512/3774/3774278.png' // Car lesson icon
      } else if (lowerTitle.includes('rent') || lowerTitle.includes('car')) {
        return 'https://cdn-icons-png.flaticon.com/512/3774/3774278.png' // Car rental icon
      }
      return 'https://cdn-icons-png.flaticon.com/512/2436/2436636.png' // Default education icon
    },
    getPackageIconAlt (title) {
      const lowerTitle = title.toLowerCase()
      if (lowerTitle.includes('bde') || lowerTitle.includes('beginner')) {
        return 'Education'
      } else if (lowerTitle.includes('ultimate')) {
        return 'Star'
      } else if (lowerTitle.includes('lesson')) {
        return 'Car Lesson'
      } else if (lowerTitle.includes('rent') || lowerTitle.includes('car')) {
        return 'Car Rental'
      }
      return 'Package'
    },
    getPackageFeatures (pkg) {
      // Parse package details from HTML and convert to array
      if (pkg.packageDetails) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = pkg.packageDetails
        const listItems = tempDiv.querySelectorAll('li')
        return Array.from(listItems).map(li => li.innerHTML)
      }
      return []
    },
    getFeatureIcon (feature) {
      const lowerFeature = feature.toLowerCase()
      if (lowerFeature.includes('free') || lowerFeature.includes('gift')) {
        return '🎁'
      }
      return '✅'
    },
    getButtonText (pkg) {
      if (pkg.isBestValue) {
        return 'Upgrade to Ultimate'
      } else if (pkg.title.toLowerCase().includes('lesson')) {
        return 'Book Lessons'
      } else if (pkg.title.toLowerCase().includes('rent')) {
        return 'Book Now'
      }
      return 'Book Now'
    },
    getButtonClass (pkg) {
      if (pkg.isPopular) {
        return 'button-popular'
      } else if (pkg.isPremium) {
        return 'button-premium'
      } else if (pkg.isBestValue || pkg.isNew) {
        return 'button-best-value'
      }
      return 'button-default'
    }
  }
}
</script>

<style>
/* Main Heading */
.main-heading {
  font-size: 22px;
  line-height: 1.4;
  font-weight: 500;
}

/* New Package Grid Layout */
.package-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0;
  padding: 0 20px;
}

/* Top row container for 2 packages */
.top-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

/* Bottom row container for 3 packages */
.bottom-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.package-card {
  position: relative;
  margin-bottom: 1rem;
}

.package-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.package-container:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Highlight border for MOST POPULAR card */
.package-container.popular-highlight {
  border: 2px solid #009966;
}

/* Badge row (badge + countdown timer) */
.badge-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 24px;
}

.offer-timer {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #0b1220;
  color: #fff;
  padding: 6px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-left: auto;
}

.offer-timer .offer-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
  display: inline-block;
}

.offer-timer .time-chip {
  background: #1f2937;
  padding: 2px 6px;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
}

.offer-timer .time-sep {
  opacity: 0.7;
}

/* "This offer ends in" banner */
.offer-ends-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fdf2f8;
  border: 1px dashed #f9a8d4;
  border-radius: 12px;
  padding: 8px 12px;
  margin: 8px 0 12px;
  flex-wrap: wrap;
}

.offer-ends-banner .offer-bolt {
  background: #a855f7;
  color: #fff;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.offer-ends-banner .offer-text {
  color: #be185d;
  font-weight: 600;
  font-size: 13px;
  margin-right: auto;
}

.offer-ends-banner .time-chip.dark {
  background: #0b1220;
  color: #fff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.offer-ends-banner .time-chip.dark sub {
  font-size: 9px;
  opacity: 0.7;
  margin-left: 2px;
  bottom: 0;
}

.offer-ends-banner .time-sep {
  color: #6b7280;
  font-weight: 700;
}

/* Badge Styles */
.package-badge {
  position: static;
  top: auto;
  left: auto;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: white;
  z-index: 10;
}

.popular-badge {
  background-color: #009966;
}

.premium-badge {
  background-color:#C800DE; /* amber-500 - golden */
}

.best-value-badge {
  background-color: #4F39F6;
}

/* Header Styles */
.package-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.package-title {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex: 1;
  line-height: 1.4;
}

.package-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-left: 12px;
}

/* Price Section */
.price-section {
  margin-bottom: 16px;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.current-price {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
}

.original-price {
  font-size: 14px;
  text-decoration: line-through;
  color: #9ca3af;
}

.save-badge {
  font-size: 12px;
  font-weight: 600;
  color: white;
  background-color: #10b981;
  padding: 2px 8px;
  border-radius: 4px;
}

.tax-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.tax-free {
  color: #059669;
  background-color: #d1fae5;
}

.tax-applicable {
  color: #dc2626;
  background-color: #fee2e2;
}

.payment-info {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Features List */
.features-list {
  list-style: none;
  margin: 0 0 24px 0;
  padding: 0;
  padding-left: 0 !important;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

.feature-icon {
  flex-shrink: 0;
  font-size: 16px;
}

/* Button Styles */
.package-button {
  margin-top: auto;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.button-popular {
  background-color: #009966;
  color: white;
}

.button-popular:hover {
  background-color: #007a4d;
  transform: scale(1.02);
}

.button-premium {
  background-color: #C800DE;
  color: white;
}

.button-premium:hover {
  background-color: #d97706;
  transform: scale(1.02);
}

.button-best-value {
  background-color: #4F39F6;
  color: white;
}

.button-best-value:hover {
  background-color: #3d2fd1;
  transform: scale(1.02);
}

.button-default {
  background-color: #1f2937;
  color: white;
}

.button-default:hover {
  background-color: #111827;
  transform: scale(1.02);
}

/* Responsive Design */

/* Offer timer / banner responsive tweaks */
@media (max-width: 768px) {
  .badge-row {
    flex-wrap: wrap;
    gap: 6px;
  }
  .offer-timer {
    font-size: 10px;
    padding: 5px 8px;
    gap: 4px;
  }
  .offer-timer .time-chip {
    padding: 2px 5px;
  }
  .offer-ends-banner {
    gap: 6px;
    padding: 6px 10px;
  }
  .offer-ends-banner .offer-text {
    font-size: 12px;
  }
  .offer-ends-banner .time-chip.dark {
    font-size: 11px;
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .offer-timer .offer-label {
    display: none;
  }
  .offer-timer {
    font-size: 10px;
    padding: 4px 8px;
  }
  .offer-ends-banner {
    padding: 6px 8px;
    gap: 4px;
  }
  .offer-ends-banner .offer-bolt {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }
  .offer-ends-banner .offer-text {
    font-size: 11px;
    flex-basis: 100%;
    margin-right: 0;
  }
  .offer-ends-banner .time-chip.dark {
    font-size: 10px;
    padding: 3px 5px;
  }
  .offer-ends-banner .time-chip.dark sub {
    font-size: 8px;
  }
}

@media (max-width: 360px) {
  .offer-timer {
    font-size: 9px;
  }
  .offer-ends-banner .time-chip.dark {
    font-size: 9px;
    padding: 2px 4px;
  }
}

@media (max-width: 1024px) {
  .main-heading {
    font-size: 20px;
  }

  .package-grid {
    padding: 0 16px;
  }

  .top-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .bottom-row {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  .package-container {
    padding: 20px;
  }

  .package-title {
    font-size: 18px;
  }

  .current-price {
    font-size: 26px;
  }
}

@media (max-width: 768px) {
  .main-heading {
    font-size: 18px;
  }

  .top-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .bottom-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .package-container {
    padding: 18px;
  }

  .package-title {
    font-size: 17px;
  }

  .current-price {
    font-size: 24px;
  }

  .package-icon {
    width: 35px;
    height: 35px;
  }

  .features-list {
    margin: 0 0 20px 0;
  }

  .feature-item {
    font-size: 13px;
    margin-bottom: 6px;
  }
}

@media (max-width: 480px) {
  .main-heading {
    font-size: 16px;
  }

  .package-grid {
    padding: 0 12px;
    gap: 16px;
  }

  .top-row {
    gap: 12px;
  }

  .bottom-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .package-container {
    padding: 16px;
  }

  .package-title {
    font-size: 16px;
  }

  .current-price {
    font-size: 22px;
  }

  .package-icon {
    width: 30px;
    height: 30px;
  }

  .package-badge {
    font-size: 10px;
    padding: 3px 8px;
    top: -8px;
    left: 16px;
  }

  .price-row {
    gap: 8px;
    margin-bottom: 6px;
  }

  .tax-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .save-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  .payment-info {
    font-size: 12px;
  }

  .features-list {
    margin: 0 0 18px 0;
  }

  .feature-item {
    font-size: 12px;
    margin-bottom: 5px;
    gap: 6px;
  }

  .feature-icon {
    font-size: 14px;
  }

  .package-button {
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 320px) {
  .main-heading {
    font-size: 14px;
  }

  .package-grid {
    padding: 0 8px;
    gap: 12px;
  }

  .top-row {
    gap: 10px;
  }

  .bottom-row {
    gap: 10px;
  }

  .package-container {
    padding: 14px;
  }

  .package-title {
    font-size: 15px;
  }

  .current-price {
    font-size: 20px;
  }

  .package-icon {
    width: 28px;
    height: 28px;
  }

  .package-badge {
    font-size: 9px;
    padding: 2px 6px;
    top: -6px;
    left: 14px;
  }

  .price-row {
    gap: 6px;
    margin-bottom: 5px;
  }

  .tax-badge {
    font-size: 9px;
    padding: 1px 5px;
  }

  .save-badge {
    font-size: 9px;
    padding: 1px 5px;
  }

  .payment-info {
    font-size: 11px;
  }

  .features-list {
    margin: 0 0 16px 0;
  }

  .feature-item {
    font-size: 11px;
    margin-bottom: 4px;
    gap: 5px;
  }

  .feature-icon {
    font-size: 13px;
  }

  .package-button {
    padding: 8px 16px;
    font-size: 13px;
  }
}

/* Keep existing styles for modal and other components */
.text-green {
  color: #2D9A47;
}

.text-white {
  color: #fff;
}

.text-black {
  color: #000;
}

.bg-green {
  background: #2D9A47;
  color: #fff;
}

.fw-normal {
  font-weight: normal;
}

.text-center {
  text-align: left;
}

.text-right {
  text-align: right;
}

.modal .paymentPlans {
  list-style: none;
  margin: 0;
  padding: 0 !important;
  position: static !important;
}

.paymentPlans li {
  padding: 5px
}

.paymentPlans li:before {
  display: none;
}

.paymentPlans .flewRow {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.paymentPlans .flewRow h4 {
  margin: 0;
}

.paymentPlans input:checked+.box {
  background: rgba(247, 248, 0, 1);
}

.paymentPlans .box {
  background: rgba(247, 248, 250, 1);
  border-radius: 15px;
  padding: 20px !important;
  display: block;
  text-align: left;
  min-height: auto !important;
}

.paymentPlans .imgBox {
  margin: 0 0 20px;
}

.paymentPlans .box .imgBox img {
  display: block;
  width: 100%;
  height: auto;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.paymentPlans .tag {
  display: block;
  padding: 0 10px;
  font-size: 12px;
  border-radius: 15px;
  background: rgba(48, 166, 248, 1);
  color: #fff;
}

.modal {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background-color: #fff;
  margin: 5% auto;
  padding: 20px;
  border-radius: 8px;
  max-width: 600px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
}

.close-btn,
.close-modal-btn {
  font-size: 18px;
  color: #333;
  border: none;
  background: none;
  cursor: pointer;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 24px;
  font-weight: bold;
}

.close-btn:hover {
  color: #ff0000;
}

.btn-green {
  background: #2D9A47 !important;
  color: #fff !important;
}

.btnRow {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 6px;
}

.btnRow .btn-green {
  flex: 0 0 48%;
  padding: 15px 30px;
  font-family: inherit;
  font-weight: 500;
  text-decoration: none !important;
  text-align: center;
  border-radius: 5px;
  line-height: 20px;
  font-size: 16px;
}

.btnRow .btn-green:hover {
  background: #045cb4;
  color: #fff;
}

/* Installment Graph Styles */
.installment-graph {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 0 10px;
}

.installment-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.installment-item.Today {
  background: #fff;
  padding: 10px;
  border-radius: 10px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.progress-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-bottom: 8px;
  position: relative;
}

.installment-text {
  font-size: 12px;
  line-height: 1.2;
}

.installment-text .amount {
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.installment-text .period {
  color: #666;
  font-size: 10px;
}
</style>
